import type { FormEvent, ReactNode } from 'react';
import {
  CAREGIVER_CERTIFICATIONS,
  CAREGIVER_EXPERTISE,
  CAREGIVER_LANGUAGES,
  US_STATES,
  WEEKDAYS,
  WORK_COUNTIES,
} from '../../../../shared/constants/application';
import type {
  CaregiverApplication,
  ClientApplication,
  UserApplication,
  WeeklyAvailability,
} from '../../types/user';
import { dedupeValues, formatUsPhone, formatZipCode } from '../../utils/applicationValidation';

const inputClass = 'mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-mcc-neutral focus:outline-none focus:ring-2 focus:ring-mcc-lightest';
const referenceNumbers = [1, 2, 3] as const;

function RequiredLabel({ children }: { children: ReactNode }) {
  return <>
    {children} <span className="text-red-700" aria-hidden="true">*</span>
  </>;
}

type CheckboxGroupProps = {
  legend: string;
  name: string;
  options: readonly string[];
  otherName: string;
  values?: string[];
};

function CheckboxGroup({ legend, name, options, otherName, values = [] }: CheckboxGroupProps) {
  const otherValue = dedupeValues(values.filter((value) => !options.includes(value))).join(', ');

  return (
    <fieldset className="sm:col-span-2">
      <legend className="text-sm">
        <RequiredLabel>{legend}</RequiredLabel>
      </legend>
      <div className="mt-2 grid gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-2">
        {options.map((option) => (
          <label key={option} className="text-sm">
            <input
              type="checkbox"
              name={name}
              value={option}
              defaultChecked={option === 'Other' ? Boolean(otherValue) : values.includes(option)}
              className="mr-2"
            />
            {option}
          </label>
        ))}
        <label className="text-sm sm:col-span-2">
          Other details
          <input className={inputClass} name={otherName} defaultValue={otherValue} maxLength={500} />
        </label>
      </div>
    </fieldset>
  );
}

type YesNoProps = {
  name: string;
  label: string;
  value?: boolean;
  requiresYes?: boolean;
};

function YesNo({ name, label, value, requiresYes = false }: YesNoProps) {
  return (
    <fieldset className="rounded-md border border-slate-200 p-3">
      <legend className="px-1 text-sm">
        <RequiredLabel>{label}</RequiredLabel>
      </legend>
      <div className="mt-1 flex gap-6 text-sm">
        <label>
          <input className="mr-2" type="radio" name={name} value="yes" defaultChecked={value === true} required />
          Yes
        </label>
        <label>
          <input className="mr-2" type="radio" name={name} value="no" defaultChecked={value === false} />
          No
        </label>
      </div>
      {requiresYes && <p className="mt-2 text-xs text-slate-600">Yes is required to submit.</p>}
    </fieldset>
  );
}

export function ContactFields({ application, email, isCaregiver }: {
  application?: UserApplication;
  email: string;
  isCaregiver: boolean;
}) {
  const maskPhone = (event: FormEvent<HTMLInputElement>) => {
    event.currentTarget.value = formatUsPhone(event.currentTarget.value);
  };
  const maskZip = (event: FormEvent<HTMLInputElement>) => {
    event.currentTarget.value = formatZipCode(event.currentTarget.value);
  };

  return (
    <>
      <h2 className="mt-7 font-semibold text-mcc-darkest">Contact information</h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <label className="text-sm">
          <RequiredLabel>First name</RequiredLabel>
          <input className={inputClass} name="firstName" defaultValue={application?.firstName} autoComplete="given-name" maxLength={80} required />
        </label>
        {isCaregiver && (
          <label className="text-sm">
            Middle name
            <input className={inputClass} name="middleName" defaultValue={application?.kind === 'caregiver' ? application.middleName : undefined} autoComplete="additional-name" maxLength={80} />
          </label>
        )}
        <label className="text-sm">
          <RequiredLabel>Last name</RequiredLabel>
          <input className={inputClass} name="lastName" defaultValue={application?.lastName} autoComplete="family-name" maxLength={80} required />
        </label>
        {isCaregiver && (
          <label className="text-sm">
            Preferred name
            <input className={inputClass} name="preferredName" defaultValue={application?.kind === 'caregiver' ? application.preferredName : undefined} maxLength={80} />
          </label>
        )}
        <label className="text-sm">
          <RequiredLabel>Phone</RequiredLabel>
          <input
            className={inputClass}
            name="phone"
            type="tel"
            defaultValue={formatUsPhone(application?.phone ?? '')}
            onInput={maskPhone}
            inputMode="tel"
            autoComplete="tel"
            placeholder="(555) 555-0123"
            pattern="\(\d{3}\) \d{3}-\d{4}"
            maxLength={18}
            required
          />
        </label>
        <label className="text-sm">
          <RequiredLabel>Email</RequiredLabel>
          <input className={`${inputClass} bg-slate-100`} name="email" type="email" autoComplete="email" value={email} readOnly maxLength={254} required />
        </label>
        <label className="text-sm sm:col-span-2">
          <RequiredLabel>Street address</RequiredLabel>
          <input className={inputClass} name="line1" defaultValue={application?.address.line1} autoComplete="address-line1" maxLength={150} required />
        </label>
        <label className="text-sm sm:col-span-2">
          Address line 2
          <input className={inputClass} name="line2" defaultValue={application?.address.line2} autoComplete="address-line2" maxLength={150} />
        </label>
        <label className="text-sm">
          <RequiredLabel>City</RequiredLabel>
          <input className={inputClass} name="city" defaultValue={application?.address.city} autoComplete="address-level2" maxLength={100} required />
        </label>
        <label className="text-sm">
          <RequiredLabel>State</RequiredLabel>
          <select className={inputClass} name="state" defaultValue={application?.address.state ?? ''} autoComplete="address-level1" required>
            <option value="">Select state</option>
            {US_STATES.map((state) => <option key={state}>{state}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <RequiredLabel>ZIP code</RequiredLabel>
          <input
            className={inputClass}
            name="postalCode"
            defaultValue={formatZipCode(application?.address.postalCode ?? '')}
            onInput={maskZip}
            inputMode="numeric"
            autoComplete="postal-code"
            pattern="\d{5}(-\d{4})?"
            maxLength={10}
            required
          />
        </label>
      </div>
    </>
  );
}

type CaregiverYesNoField =
  | 'hasDriversLicense'
  | 'hasReliableTransportation'
  | 'willingToTransport'
  | 'insured'
  | 'authorizedToWork'
  | 'legallyPermitted'
  | 'backgroundCheckConsent'
  | 'independentContractorAcknowledgment';

const yesNoQuestions: { name: CaregiverYesNoField; label: string; requiresYes?: boolean }[] = [
  { name: 'hasDriversLicense', label: "I have a valid driver's license" },
  { name: 'hasReliableTransportation', label: 'I have reliable transportation' },
  { name: 'willingToTransport', label: 'I am willing to transport clients when permitted and insured' },
  { name: 'insured', label: 'I carry applicable liability insurance' },
  { name: 'authorizedToWork', label: 'I am authorized to work in the United States of America', requiresYes: true },
  { name: 'legallyPermitted', label: 'I am legally permitted to provide the services offered', requiresYes: true },
  { name: 'backgroundCheckConsent', label: 'I consent to required background and reference checks', requiresYes: true },
  { name: 'independentContractorAcknowledgment', label: 'I understand this is an independent caregiver referral application and not employment', requiresYes: true },
];

export function CaregiverFields({ application }: { application?: CaregiverApplication }) {
  const savedAvailability = application?.availability as WeeklyAvailability | string | undefined;
  const availability = typeof savedAvailability === 'object' ? savedAvailability : undefined;
  const legacyAvailability = typeof savedAvailability === 'string' ? savedAvailability : undefined;
  const savedServiceAreas = dedupeValues(application?.serviceAreas ?? []);
  const selectedServiceArea = savedServiceAreas.find((area) => (WORK_COUNTIES as readonly string[]).includes(area)) ?? '';

  return (
    <>
      <h2 className="mt-7 font-semibold text-mcc-darkest">Experience and availability</h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <label className="text-sm">
          <RequiredLabel>Date of birth</RequiredLabel>
          <input className={inputClass} name="dateOfBirth" defaultValue={application?.dateOfBirth} type="date" required />
        </label>
        <label className="text-sm">
          <RequiredLabel>Years of experience</RequiredLabel>
          <input className={inputClass} name="yearsExperience" defaultValue={application?.yearsExperience} type="number" min="0" max="80" step="1" required />
        </label>
        <CheckboxGroup legend="Languages spoken (select all that apply)" name="languages" options={CAREGIVER_LANGUAGES} otherName="otherLanguage" values={application?.languages} />
        <CheckboxGroup legend="Areas of expertise (select all that apply)" name="servicesOffered" options={CAREGIVER_EXPERTISE} otherName="otherExpertise" values={application?.servicesOffered} />
        <CheckboxGroup legend="Certifications (select all that apply)" name="certifications" options={CAREGIVER_CERTIFICATIONS} otherName="otherCertification" values={application?.certifications} />
        <label className="text-sm sm:col-span-2">
          <RequiredLabel>Professional bio</RequiredLabel>
          <textarea
            className={inputClass}
            name="caregivingApproach"
            defaultValue={application?.caregivingApproach}
            rows={4}
            maxLength={2000}
            placeholder="Tell us about your experience, approach to care, and what makes you a great caregiver."
            required
          />
        </label>
        <div>
          <label className="text-sm">
            <RequiredLabel>County of preference for work</RequiredLabel>
            <select className={inputClass} name="serviceAreas" defaultValue={selectedServiceArea} required>
              <option value="">Select county</option>
              {WORK_COUNTIES.map((county) => <option key={county}>{county}</option>)}
            </select>
          </label>
          {savedServiceAreas.length > 1 && (
            <p className="mt-2 text-sm text-amber-800" role="status">
              This profile lists multiple service areas ({savedServiceAreas.join(', ')}). Select one county; saving will replace the previous list.
            </p>
          )}
        </div>
        <label className="text-sm">
          <RequiredLabel>Maximum travel distance</RequiredLabel>
          <span className="flex items-baseline gap-2">
            <input className={inputClass} name="maximumTravelMiles" defaultValue={application?.maximumTravelMiles} type="number" min="0" max="500" step="1" required />
            <span>miles</span>
          </span>
        </label>
        <label className="text-sm sm:col-span-2">
          <RequiredLabel>Prior care settings (separate multiple settings with commas)</RequiredLabel>
          <input className={inputClass} name="priorCareSettings" defaultValue={dedupeValues(application?.priorCareSettings ?? []).join(', ')} maxLength={500} placeholder="Private home, agency, hospice, etc." required />
        </label>
        <fieldset className="sm:col-span-2">
          <legend className="text-sm">
            <RequiredLabel>Available work hours</RequiredLabel>
          </legend>
          {legacyAvailability && (
            <p className="mt-1 text-sm text-slate-600">
              Previous availability: {legacyAvailability}. Please enter availability for each day.
            </p>
          )}
          <div className="mt-2 grid gap-3 rounded-md border border-slate-200 p-3 sm:grid-cols-2">
            {WEEKDAYS.map((day) => (
              <label key={day} className="text-sm capitalize">
                {day}
                <input className={inputClass} name={`availability-${day}`} defaultValue={availability?.[day]} maxLength={100} placeholder="e.g. 8:00 AM–5:00 PM or unavailable" required />
              </label>
            ))}
          </div>
        </fieldset>
        <label className="text-sm sm:col-span-2">
          Service limitations and tasks you do not perform
          <textarea className={inputClass} name="serviceLimitations" defaultValue={application?.serviceLimitations} rows={3} maxLength={1000} placeholder="Please list any work tasks you are unable to perform." />
        </label>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {yesNoQuestions.map(({ name, label, requiresYes }) => (
          <YesNo key={name} name={name} label={label} value={application?.[name]} requiresYes={requiresYes} />
        ))}
      </div>
      <h2 className="mt-7 font-semibold text-mcc-darkest">References and certification</h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-3">
        {referenceNumbers.map((number) => {
          const reference = application?.references[number - 1];
          return (
            <fieldset key={number} className="rounded-md border border-slate-200 p-3">
              <legend className="px-1 text-sm font-semibold">Reference {number}</legend>
              <input className={inputClass} name={`reference${number}Name`} defaultValue={reference?.name} placeholder="Name" aria-label={`Reference ${number} name`} maxLength={100} required />
              <input className={inputClass} name={`reference${number}Relationship`} defaultValue={reference?.relationship} placeholder="Relationship" aria-label={`Reference ${number} relationship`} maxLength={100} required />
              <input className={inputClass} name={`reference${number}Contact`} defaultValue={reference?.contact} placeholder="Phone or email" aria-label={`Reference ${number} phone or email`} maxLength={254} required />
            </fieldset>
          );
        })}
      </div>
      <label className="mt-4 block text-sm">
        <RequiredLabel>Printed name</RequiredLabel>
        <input className={inputClass} name="printedName" defaultValue={application?.printedName} maxLength={160} required />
      </label>
    </>
  );
}

export function ClientFields({ application }: { application?: ClientApplication }) {
  return (
    <>
      <h2 className="mt-7 font-semibold text-mcc-darkest">Care needs</h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <label className="text-sm">
          <RequiredLabel>Care recipient's name</RequiredLabel>
          <input className={inputClass} name="careRecipientName" defaultValue={application?.careRecipientName} maxLength={160} required />
        </label>
        <label className="text-sm">
          <RequiredLabel>Your relationship</RequiredLabel>
          <input className={inputClass} name="relationshipToRecipient" defaultValue={application?.relationshipToRecipient} maxLength={100} required />
        </label>
        <label className="text-sm sm:col-span-2">
          <RequiredLabel>Requested services</RequiredLabel>
          <input
            className={inputClass}
            name="requestedServices"
            defaultValue={dedupeValues(application?.requestedServices ?? []).join(', ')}
            maxLength={500}
            placeholder="Separate multiple services with commas"
            required
          />
        </label>
        <label className="text-sm sm:col-span-2">
          Additional care notes
          <textarea className={inputClass} name="careNotes" defaultValue={application?.careNotes} rows={4} maxLength={2000} />
        </label>
      </div>
    </>
  );
}
